import { computed, isRef, toValue } from 'vue';
import VarType from 'Js/enums/varType';
export function useDataGridSelection(options = {}) {
    const getKey = () => toValue(options.itemKey) ?? 'Id';
    function resolveItemSelectable(item) {
        const raw = options.itemSelectable;
        if (isRef(raw)) {
            return raw.value;
        }
        if (typeof raw === 'function') {
            // If it's a 0-argument getter (e.g. () => props.itemSelectable)
            // or calling it returns a string / function / undefined, evaluate it.
            // But if it's a predicate function (item) => boolean, calling with 0 args returns boolean or undefined.
            // A standard getter like () => props.itemSelectable has raw.length === 0.
            if (raw.length === 0) {
                return raw();
            }
            return raw;
        }
        return raw;
    }
    function itemCannotBeSelected(item) {
        const selectable = resolveItemSelectable(item);
        if (!selectable)
            return false;
        if (typeof selectable === VarType.String) {
            return !item[selectable];
        }
        if (typeof selectable === VarType.Function) {
            return !selectable(item);
        }
        return false;
    }
    function isSelected(item) {
        const key = getKey();
        const current = toValue(options.selectedItems) || [];
        return current.some((i) => i[key] === item[key]);
    }
    function toggleSelectItem(item) {
        if (itemCannotBeSelected(item))
            return;
        const key = getKey();
        const isSingle = toValue(options.singleSelect);
        if (isSingle) {
            const isCurrentlySelected = isSelected(item);
            const newSelected = isCurrentlySelected ? [] : [item];
            options.onUpdateSelectedItems?.(newSelected);
            return;
        }
        const current = [...(toValue(options.selectedItems) || [])];
        const index = current.findIndex((i) => i[key] === item[key]);
        if (index > -1) {
            current.splice(index, 1);
        }
        else {
            current.push(item);
        }
        options.onUpdateSelectedItems?.(current);
    }
    const allSelected = computed(() => {
        const displayList = toValue(options.items) || [];
        const selectableDisplay = displayList.filter((i) => !itemCannotBeSelected(i));
        return selectableDisplay.length > 0 && selectableDisplay.every((i) => isSelected(i));
    });
    const someSelected = computed(() => {
        const current = toValue(options.selectedItems) || [];
        return current.length > 0 && !allSelected.value;
    });
    function toggleSelectAll(value) {
        const displayList = toValue(options.items) || [];
        const selectableDisplay = displayList.filter((i) => !itemCannotBeSelected(i));
        const current = toValue(options.selectedItems) || [];
        const key = getKey();
        // If explicit boolean parameter is passed (e.g. from EnDataGrid or checkbox update)
        if (typeof value === 'boolean') {
            const newSelected = value ? [...selectableDisplay] : [];
            options.onUpdateSelectedItems?.(newSelected);
            return;
        }
        // Toggle behavior when no boolean parameter is passed (e.g. header click in EnSimpleDataGrid)
        if (allSelected.value) {
            const remaining = current.filter((item) => !selectableDisplay.some((disp) => disp[key] === item[key]));
            options.onUpdateSelectedItems?.(remaining);
        }
        else {
            const toAdd = selectableDisplay.filter((item) => !isSelected(item));
            options.onUpdateSelectedItems?.([...current, ...toAdd]);
        }
    }
    return {
        itemCannotBeSelected,
        isSelected,
        toggleSelectItem,
        toggleSelectAll,
        allSelected,
        someSelected,
    };
}
